import { useEffect, useState } from 'react'
import { getCardById } from '../data/ingredientCards'
import { IngredientCardImage } from '../components/IngredientCardImage'
import { useT } from '../../../i18n'

const MAX_CHARS = 280

export interface CourseData {
  text: string
  usedCards: string[]
}

interface Props {
  /** Cards still available for this course (already filtered by parent). */
  hand: string[]
  /** Which course this round is about (0 = appetizer, 1 = main, 2 = dessert). */
  courseIndex: 0 | 1 | 2
  /** When true (dessert round), every remaining card MUST be used. */
  mustUseAll?: boolean
  onSubmit: (course: CourseData) => void
}

const COURSE_NAMES = ['Appetizer', 'Main Course', 'Dessert']
const COURSE_EMOJIS = ['🥗', '🍽️', '🍮']
const COURSE_HINTS = [
  'Start with something intriguing…',
  'The heart of your meal. Make it memorable.',
  'Use everything you have left. Go out with a bang!',
]

/**
 * Single-course story builder. The room's phase machine drives which
 * course is active, so we only need to render one form at a time:
 * appetizer → vote → main → vote → dessert. Local state resets every
 * time the parent re-mounts us (on phase change).
 *
 * Ingredients used in earlier courses are already removed from `hand`
 * by the parent. In the dessert round (`mustUseAll`), all remaining
 * cards are pre-selected and locked — you have to use what's left.
 */
export function StoryBuilderScreen({ hand, courseIndex, mustUseAll = false, onSubmit }: Props) {
  const t = useT()
  const [text, setText] = useState('')
  const [usedCards, setUsedCards] = useState<string[]>([])

  // When the course changes or mustUseAll kicks in, reset the draft.
  useEffect(() => {
    setText('')
    setUsedCards(mustUseAll ? [...hand] : [])
  }, [courseIndex, mustUseAll])

  // If mustUseAll and hand changes (e.g. bonus ingredient arrived),
  // re-sync the forced selection.
  useEffect(() => {
    if (mustUseAll) setUsedCards([...hand])
  }, [hand.length, mustUseAll])

  const cards = hand.map(id => getCardById(id)).filter(Boolean)
  // Require a story AND at least one ingredient card — otherwise it's too
  // easy to write a dish and forget to mark the cards you used, which then
  // never show on the present screen. (If the hand is somehow empty the
  // card requirement is waived so the course can't brick.)
  const canSubmit = text.trim().length > 0 && (usedCards.length > 0 || hand.length === 0)
  const needsCard = text.trim().length > 0 && usedCards.length === 0 && hand.length > 0

  // Later courses each need at least one card too — don't let this
  // course swallow the whole hand before dessert.
  const coursesLeft = 2 - courseIndex
  const atReserveCap = !mustUseAll && hand.length - usedCards.length <= coursesLeft

  function toggleCard(id: string) {
    if (mustUseAll) return // can't deselect in dessert round
    setUsedCards(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id)
      if (hand.length - prev.length <= coursesLeft) return prev
      return [...prev, id]
    })
  }

  return (
    <div className="screen">
      {/* Course indicator — shows progress through the 3-course arc. */}
      <div className="flex bg-brand-elevated border-b border-brand-border">
        {COURSE_NAMES.map((name, i) => {
          const isActive = i === courseIndex
          const isDone = i < courseIndex
          return (
            <div
              key={i}
              className={`flex-1 py-3 text-sm font-medium text-center transition-colors
                ${isActive
                  ? 'text-brand-primary border-b-2 border-brand-primary bg-white'
                  : isDone
                    ? 'text-brand-muted'
                    : 'text-brand-muted/60'}`}
            >
              <span className="mr-1">{COURSE_EMOJIS[i]}</span>
              {name}
              {isDone && <span className="ml-1 text-brand-success text-xs">✓</span>}
            </div>
          )
        })}
      </div>

      <div className="flex-1 flex flex-col px-5 py-5 gap-4 overflow-y-auto pb-32">
        <div>
          <h2 className="font-display text-xl font-bold text-brand-text">
            {COURSE_EMOJIS[courseIndex]} {COURSE_NAMES[courseIndex]}
          </h2>
          <p className="text-sm text-brand-muted mt-0.5">{COURSE_HINTS[courseIndex]}</p>
          <p className="text-xs text-brand-muted mt-1">
            {mustUseAll
              ? t('All your remaining ingredients must be used in this course.')
              : t('Write this one, everyone votes, then we move to the next course.')}
          </p>
        </div>

        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {mustUseAll
              ? (cards.length === 1
                  ? t('Use all 1 remaining ingredient')
                  : t('Use all {n} remaining ingredients', { n: cards.length }))
              : t('Tap to include in this course')}
          </p>
          <div className="flex flex-wrap gap-3">
            {cards.map(card => card && (
              <IngredientCardImage
                key={card.id}
                id={card.id}
                size="md"
                selected={usedCards.includes(card.id) || mustUseAll}
                onSelect={mustUseAll ? undefined : toggleCard}
              />
            ))}
          </div>
          {atReserveCap && coursesLeft > 0 && (
            <p className="text-[11px] text-brand-muted mt-2">
              {coursesLeft === 1
                ? t('Save 1 card for the course after this one.')
                : t('Save {n} cards for the courses after this one.', { n: coursesLeft })}
            </p>
          )}
        </div>

        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Your story')}
          </p>
          <div className="relative">
            <textarea
              value={text}
              onChange={e => setText(e.target.value.slice(0, MAX_CHARS))}
              placeholder={t('Describe your {course}. Be creative, be weird, be delicious!', { course: COURSE_NAMES[courseIndex].toLowerCase() })}
              rows={5}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                         text-brand-text placeholder:text-brand-muted/60 resize-none
                         focus:outline-hidden focus:border-brand-primary focus:ring-2
                         focus:ring-brand-primary/20 transition-all text-sm"
            />
            <div className={`absolute bottom-3 right-4 text-xs font-medium
              ${text.length > MAX_CHARS * 0.9 ? 'text-brand-danger' : 'text-brand-muted'}`}>
              {text.length}/{MAX_CHARS}
            </div>
          </div>
        </div>
      </div>

      {needsCard && (
        <div className="floating-actions">
          <p className="text-center text-sm font-semibold text-brand-danger
                        bg-white rounded-2xl py-3 px-4 shadow-card">{t('Tap the ingredient card(s) you used in your dish')}</p>
        </div>
      )}
      {canSubmit && (
        <div className="floating-actions animate-slide-up">
          <button
            onClick={() => onSubmit({ text: text.trim(), usedCards })}
            className="btn-primary bg-brand-success hover:bg-brand-success"
          >
            {courseIndex < 2
              ? t('Submit {course} →', { course: COURSE_NAMES[courseIndex] })
              : t('✅ Submit Dessert (Final Course!)')}
          </button>
        </div>
      )}
    </div>
  )
}
